import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { trackError } from 'utils/helpers';
import ErrorDialog from 'components/error_dialog';
import LoadingButton from 'components/loading_button/loading_button';
import {
  replaceBooking,
  restoreOriginalBooking,
  setOriginalBooking,
} from 'redux/actions/booking';
import {
  useDiscardDraftMutation,
  useLazyDraftFeeQuery,
  useLazyGetDraftChangesQuery,
  useLazyGetDraftQuery,
} from 'redux/api_slice/booking';
import { useDeleteMutation } from 'redux/api_slice/reservation';
import {
  setAmendmentFee,
  setCancellationFee,
  setChanges as setDraftChanges,
  setFees,
  setIsGeneratingDraftBooking,
  setOweUserMoney,
  setShow as setShowDraftChangeSummary,
} from 'redux/slice/draft_summary_of_changes';

export const CancelTrip = ({ reservationIds: originalReservationIds }) => {
  const primaryBookingId = Number(useParams().booking_id);
  const [cancelling, setCancelling] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [deleteReservation] = useDeleteMutation();
  const [getDraftChanges] = useLazyGetDraftChangesQuery();
  const [discardDraft] = useDiscardDraftMutation();
  const dispatch = useDispatch();
  const [getDraftBooking] = useLazyGetDraftQuery();
  const [getDraftFee] = useLazyDraftFeeQuery();
  const { isGeneratingDraftBooking } = useSelector(
    (state) => state.draftSummaryOfChanges
  );

  const requestCanceltrip = async () => {
    setCancelling(true);
    dispatch(setIsGeneratingDraftBooking(true));

    try {
      // Discard potential draft booking in the backend, we can't use any existing draft booking
      await discardDraft(primaryBookingId).unwrap();

      // Create the draft booking
      const { draft: draftBooking, original: originalBooking } =
        await getDraftBooking(primaryBookingId).unwrap();

      // Get the corresponding reservation ids in the draft booking
      const draftReservationIds = draftBooking.reservations_attributes
        .filter((r) => originalReservationIds.includes(r.reservation_a_id))
        .map((r) => r.id);

      // Make the changes in the draft booking
      // eslint-disable-next-line no-restricted-syntax
      for (const reservationId of draftReservationIds) {
        // eslint-disable-next-line no-await-in-loop
        await deleteReservation({
          bookingId: draftBooking.id,
          reservationId,
        }).unwrap();
      }

      // Show the changes dialog to the user
      const draftChanges = await getDraftChanges(draftBooking.id).unwrap();

      // Pull fees
      const draftFee = await getDraftFee(primaryBookingId).unwrap();

      // Update the store with the draft booking, the DraftSummaryOfChanges component relies on the draft booking object to work
      dispatch(replaceBooking(draftBooking));
      dispatch(setOriginalBooking(originalBooking));
      dispatch(setShowDraftChangeSummary(true));
      dispatch(setDraftChanges(draftChanges.changes));
      dispatch(setFees(draftFee));
      dispatch(setAmendmentFee(draftFee));
      dispatch(setCancellationFee(draftFee));
      dispatch(setOweUserMoney(draftChanges.changes));
      dispatch(setIsGeneratingDraftBooking(false));
      setCancelling(false);
    } catch (error) {
      setErrorMessage(error.data?.message || 'An error occurred');
      dispatch(restoreOriginalBooking());
      dispatch(setIsGeneratingDraftBooking(false));
      trackError(error);
      setCancelling(false);
    }
  };

  return (
    <>
      <LoadingButton
        type="button"
        className="link-danger text-decoration-underline"
        disabled={cancelling || isGeneratingDraftBooking}
        loading={cancelling}
        onClick={requestCanceltrip}
      >
        {cancelling ? 'Cancelling...' : 'Cancel trip'}
      </LoadingButton>
      <ErrorDialog
        handleClose={() => setErrorMessage(null)}
        show={Boolean(errorMessage)}
      >
        {errorMessage}
      </ErrorDialog>
    </>
  );
};

CancelTrip.propTypes = {
  reservationIds: PropTypes.arrayOf(PropTypes.number),
};
